import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { CreateLines, CreateLineSystem } from "@babylonjs/core/Meshes/Builders/linesBuilder";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Vector2, Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Plane } from "@babylonjs/core/Maths/math.plane";
import { BoundingBox } from "../../core/mdl/BoundingBox";
import { ElementCategory } from "../../core/mdl/SceneElement";
import { intersectRayPlane, VECTOR3_Y } from "../../core/utils/MathUtils";
import { CameraMode } from "../../core/view/CameraControl";
import { CoordinatesType } from "../../core/view/View";
import { contains } from "../../utils/array";
import { ERR_INVALID_ARGUMENT } from "../../utils/error";
import { clamp } from "../../utils/math";
/**
 * Creates a line based grid.
 * @param pScene BabylonJS scene
 * @param pSize Size of the grid in meter.
 * @param pLineDistance Distance between the grid lines in meter. (default: 0.1)
 */
export function createGrid(pScene, pSize, pLineDistance = 0.1) {
    if (pScene == null)
        throw new Error(ERR_INVALID_ARGUMENT);
    const tGrid = new Mesh("grid", pScene);
    tGrid.freezeWorldMatrix();
    const tHS = pSize * 0.5;
    const tColor = new Color3(0.0, 0.0, 0.0);
    const tAlpha1 = 0.16;
    const tAlpha2 = 0.08;
    //big steps (default = 1.0)
    const tColor1Lines = new Array();
    const tBigDistance = pLineDistance * 10;
    for (let i = -tHS; i <= tHS; i += tBigDistance) {
        tColor1Lines.push([
            new Vector3(-tHS, 0, i),
            new Vector3(tHS, 0, i)
        ]);
        tColor1Lines.push([
            new Vector3(i, 0, -tHS),
            new Vector3(i, 0, tHS)
        ]);
    }
    let tLineMesh = CreateLineSystem("gridLines1", { lines: tColor1Lines, updatable: false, instance: null }, pScene);
    tLineMesh.color = tColor;
    tLineMesh.alpha = tAlpha1;
    tLineMesh.isPickable = false;
    tLineMesh.parent = tGrid;
    tLineMesh.freezeWorldMatrix();
    //small steps (default = 0.1)
    const tColor2Lines = new Array();
    let tCount = 0; //used to skip every 10th line
    for (let i = -tHS; i <= tHS; i += pLineDistance) {
        tCount++;
        if (tCount === 1 || tCount === 11) {
            if (tCount === 11)
                tCount = 1;
            continue;
        }
        tColor2Lines.push([
            new Vector3(-tHS, 0, i),
            new Vector3(tHS, 0, i)
        ]);
        tColor2Lines.push([
            new Vector3(i, 0, -tHS),
            new Vector3(i, 0, tHS)
        ]);
    }
    tLineMesh = CreateLineSystem("gridLines2", { lines: tColor2Lines, updatable: false, instance: null }, pScene);
    tLineMesh.color = tColor;
    tLineMesh.alpha = tAlpha2;
    tLineMesh.isPickable = false;
    tLineMesh.parent = tGrid;
    tLineMesh.freezeWorldMatrix();
    return (tGrid);
}
export function createAxisTrihedron(pScene, pSize = 0.1) {
    if (pScene == null)
        throw new Error(ERR_INVALID_ARGUMENT);
    const tTri = new Mesh("axisTrihedron", pScene);
    tTri.freezeWorldMatrix();
    const tAxisX = CreateLines("axisTrihedronX", {
        points: [
            new Vector3(0, 0, 0),
            new Vector3(pSize, 0, 0)
        ],
        updatable: false,
        instance: null
    }, pScene);
    tAxisX.color = new Color3(1, 0, 0);
    tAxisX.alpha = 0.5;
    tAxisX.isPickable = false;
    tAxisX.parent = tTri;
    tAxisX.freezeWorldMatrix();
    tAxisX.renderingGroupId = 1;
    const tAxisY = CreateLines("axisTrihedronY", {
        points: [
            new Vector3(0, 0, 0),
            new Vector3(0, pSize, 0)
        ],
        updatable: false,
        instance: null
    }, pScene);
    tAxisY.color = new Color3(0, 1, 0);
    tAxisY.alpha = 0.5;
    tAxisY.isPickable = false;
    tAxisY.parent = tTri;
    tAxisY.freezeWorldMatrix();
    tAxisY.renderingGroupId = 1;
    const tAxisZ = CreateLines("axisTrihedronZ", {
        points: [
            new Vector3(0, 0, 0),
            new Vector3(0, 0, pSize)
        ],
        updatable: false,
        instance: null
    }, pScene);
    tAxisZ.color = new Color3(0, 0, 1);
    tAxisZ.alpha = 0.5;
    tAxisZ.isPickable = false;
    tAxisZ.parent = tTri;
    tAxisZ.freezeWorldMatrix();
    tAxisZ.renderingGroupId = 1;
    return (tTri);
}
/**
 * Places the elements in the center of the given view and tries to avoid collusion with other elements.
 * The elements will be centered at the found position. The positioning is done in the XZ-plane only.
 * The y-coordinate of the elements will not be modified.
 */
export function placeElementsInViewCenter(pElements, pView, pModel, pOptions) {
    if (pElements == null || pElements.length === 0)
        return;
    //setup options
    const tOptions = pOptions != null ? pOptions : {};
    if (tOptions.placingAreaMin == null)
        tOptions.placingAreaMin = new Vector2(-5, -5);
    if (tOptions.placingAreaMax == null)
        tOptions.placingAreaMax = new Vector2(5, 5);
    if (tOptions.snapGridSize == null) {
        tOptions.snapGridSize = new Vector2(pModel.app.settings.snapping.gridSize.x, pModel.app.settings.snapping.gridSize.z);
    }
    //pick XZ plane from view center
    const tTotalBounds = BoundingBox.FromElements(pElements, undefined, true);
    const tCenter = tTotalBounds.getCenter();
    let tPickPos = null;
    let tPickPlane = null;
    if (pView.cameraControl.cameraMode == CameraMode.Orthographic ||
        pView.cameraControl.cameraMode == CameraMode.Perspective) {
        tPickPlane = Plane.FromPositionAndNormal(tCenter, VECTOR3_Y);
    }
    else {
        tPickPlane = Plane.FromPositionAndNormal(tCenter, pView.cameraControl.direction);
    }
    const tRay = pView.createPickingRay(new Vector2(pView.width * 0.5, pView.height * 0.5), CoordinatesType.Viewer);
    tPickPos = intersectRayPlane(tRay, tPickPlane);
    if (tPickPos == null)
        tPickPos = Vector3.Zero();
    //limit position to placing area
    tPickPos.x = clamp(tPickPos.x, tOptions.placingAreaMin.x, tOptions.placingAreaMax.x);
    tPickPos.z = clamp(tPickPos.z, tOptions.placingAreaMin.y, tOptions.placingAreaMax.y);
    //snap position to grid
    if (pModel.app.settings.snapping.gridEnabled || (pOptions != null && pOptions.snapGridSize != null)) {
        tPickPos.x = tOptions.snapGridSize.x * (Math.round(tPickPos.x / tOptions.snapGridSize.y));
        tPickPos.z = tOptions.snapGridSize.y * (Math.round(tPickPos.z / tOptions.snapGridSize.y));
    }
    placeElementsAtFreePosition(pElements, new Vector2(tPickPos.x, tPickPos.z), pModel);
}
/**
 * Places the elements at the desired position and tries to avoid collusion with other elements.
 * The elements will be centered at the found position. The positioning is done in the XZ-plane only.
 * The y-coordinate of the elements will not be modified.
 * @pDesiredPos: Desired position of the elements given as a 2D vector on the XZ-plane.
 */
export function placeElementsAtFreePosition(pElements, pDesiredPos, pModel) {
    if (pElements == null || pElements.length === 0)
        return;
    //get total bounds of the elements
    const tTotalBounds = BoundingBox.FromElements(pElements, undefined, true);
    const tOrgCenter3d = tTotalBounds.getCenter();
    const tOrgCenter = new Vector2(tOrgCenter3d.x, tOrgCenter3d.z);
    //move bounds to desired position
    let tVDelta = pDesiredPos.subtract(tOrgCenter);
    const tVDelta3d = new Vector3(tVDelta.x, 0, tVDelta.y);
    tTotalBounds.min.addInPlace(tVDelta3d);
    tTotalBounds.max.addInPlace(tVDelta3d);
    const tTotalBoundsCenter = tTotalBounds.getCenter();
    //current bounds positions
    const tCP1 = new Vector2(tTotalBounds.min.x, tTotalBounds.min.z);
    const tCP2 = new Vector2(tTotalBounds.min.x, tTotalBounds.max.z);
    const tCP3 = new Vector2(tTotalBounds.max.x, tTotalBounds.max.z);
    const tCP4 = new Vector2(tTotalBounds.max.x, tTotalBounds.min.z);
    //Get bounding boxes of other elements in the scene which may intersect with the placed elememts.
    const tElementBounds = new Array();
    for (const tElement of pModel.elements) {
        //ignore floor plan
        if (tElement.category === ElementCategory.FloorPlan)
            continue;
        if (!contains(pElements, tElement)) {
            const tBounds = tElement.boundingBoxVisible.transform(tElement.transform);
            //check if element is over or under the placed elements
            if (tBounds.max.y < tTotalBounds.min.y || tBounds.min.y > tTotalBounds.max.y)
                continue;
            const tCenter = tBounds.getCenter();
            const tCenterOffset = new Vector2(tCenter.x - tTotalBoundsCenter.x, tCenter.z - tTotalBoundsCenter.z);
            tElementBounds.push({ bounds: tBounds, centerOffset: tCenterOffset });
        }
    }
    //sort the bounding boxes, nearest come first
    //priority is: right, left, front, back (+x, -x, -y, +y)
    const tTempA = Vector2.Zero();
    const tTempB = Vector2.Zero();
    tElementBounds.sort((a, b) => {
        tTempA.x = a.centerOffset.x > 0.0 ? a.centerOffset.x * 0.97 : a.centerOffset.x * 0.98;
        tTempA.y = a.centerOffset.y < 0.0 ? a.centerOffset.y * 0.99 : a.centerOffset.y;
        tTempB.x = b.centerOffset.x > 0.0 ? b.centerOffset.x * 0.97 : b.centerOffset.x * 0.98;
        tTempB.y = b.centerOffset.y < 0.0 ? b.centerOffset.y * 0.99 : b.centerOffset.y;
        return (tTempA.lengthSquared() - tTempB.lengthSquared());
    });
    //Collect all potential element positions (as offset)
    //The given elements will be placed next to other elements in the scene
    const tOffsets = new Array();
    tOffsets.push({ source: null, offset: Vector2.Zero() }); //add desired pos as first candidate
    for (const tEBounds of tElementBounds) {
        const tBounds = tEBounds.bounds;
        //target bounds positions
        const tTP1 = new Vector2(tBounds.min.x, tBounds.min.z);
        const tTP2 = new Vector2(tBounds.min.x, tBounds.max.z);
        const tTP3 = new Vector2(tBounds.max.x, tBounds.max.z);
        const tTP4 = new Vector2(tBounds.max.x, tBounds.min.z);
        //right
        tOffsets.push({ source: tBounds, offset: tTP3.subtract(tCP2) });
        tOffsets.push({ source: tBounds, offset: tTP4.subtract(tCP1) });
        //left
        tOffsets.push({ source: tBounds, offset: tTP1.subtract(tCP4) });
        tOffsets.push({ source: tBounds, offset: tTP2.subtract(tCP3) });
        //front
        tOffsets.push({ source: tBounds, offset: tTP4.subtract(tCP3) });
        tOffsets.push({ source: tBounds, offset: tTP1.subtract(tCP2) });
        //back
        tOffsets.push({ source: tBounds, offset: tTP2.subtract(tCP1) });
        tOffsets.push({ source: tBounds, offset: tTP3.subtract(tCP4) });
    }
    //find the first offset which does not intersect with any of collected bounds
    let tFoundOffset = null;
    for (const tOffset of tOffsets) {
        const tOffsetBounds = tTotalBounds.clone();
        const tOffset3d = new Vector3(tOffset.offset.x, 0, tOffset.offset.y);
        tOffsetBounds.min.addInPlace(tOffset3d);
        tOffsetBounds.max.addInPlace(tOffset3d);
        tFoundOffset = tOffset.offset;
        for (const tOtherBounds of tElementBounds) {
            if (tOtherBounds.bounds === tOffset.source)
                continue;
            if (tOtherBounds.bounds.intersects(tOffsetBounds, false)) {
                tFoundOffset = null;
                break;
            }
        }
        if (tFoundOffset != null)
            break;
    }
    if (tFoundOffset == null)
        tFoundOffset = Vector2.Zero();
    //move elements to the found position
    tVDelta = pDesiredPos.add(tFoundOffset).subtract(tOrgCenter);
    try {
        pModel.eventBeginTransformElements.trigger();
        for (const tElement of pElements) {
            tElement.node.position.x += tVDelta.x;
            tElement.node.position.z += tVDelta.y;
            tElement.updateTransformation();
        }
    }
    finally {
        pModel.eventEndTransformElements.trigger();
    }
}
